import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem('app-language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const languages = [
    { code: 'it', label: 'IT' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
  ];

  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('app-language', lang);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavLink = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (location.pathname.startsWith('/social')) {
      return;
    }
    event.preventDefault();
    scrollToSection(id);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-[rgba(12,14,44,0.9)] border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/">
            <img
              src="/logo.jpeg"
              alt={t('nav.brandAlt', 'RegAI logo')}
              className="h-10 w-auto rounded-xl shadow"
              loading="lazy"
              decoding="async"
            />
          </Link>
          <div>
            <div className="text-sm uppercase tracking-widest text-regai-accent font-semibold">
              RegAI
            </div>
            <div className="font-bold leading-4 text-sm text-white">
              {t('nav.tagline')}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/80" aria-label="Main navigation">
            <Link
              to="/#percorsi"
              onClick={(event) => handleNavLink(event, 'percorsi')}
              className="hover:text-regai-accent transition-colors"
            >
              {t('nav.percorsi')}
            </Link>
            <Link
              to="/#vantaggi"
              onClick={(event) => handleNavLink(event, 'vantaggi')}
              className="hover:text-regai-accent transition-colors"
            >
              {t('nav.vantaggi')}
            </Link>
            <Link
              to="/#kpi"
              onClick={(event) => handleNavLink(event, 'kpi')}
              className="hover:text-regai-accent transition-colors"
            >
              {t('nav.kpi')}
            </Link>
            <Link
              to="/#faq"
              onClick={(event) => handleNavLink(event, 'faq')}
              className="hover:text-regai-accent transition-colors"
            >
              {t('nav.faq')}
            </Link>
            <Link
              to="/social"
              className="hover:text-regai-accent transition-colors"
            >
              Social
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-1 rounded-full border border-white/10 bg-white/10 p-1"
              role="group"
              aria-label={t('nav.language')}
            >
              {languages.map((language) => {
                const isActive = currentLanguage === language.code;
                return (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => handleLanguageChange(language.code)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                      isActive
                        ? 'bg-regai text-white shadow'
                        : 'text-white/70 hover:text-white'
                    }`}
                    aria-pressed={isActive}
                    aria-label={t('nav.switchTo', { lang: language.label })}
                  >
                    {language.label}
                  </button>
                );
              })}
            </div>

            <Link
              to="/#form"
              onClick={(event) => handleNavLink(event, 'form')}
              className="btn btn-primary"
              aria-label={t('nav.cta')}
            >
              {t('nav.cta')}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
